// Patient-initiated org sharing (ACDPA-186).
// Calls the KERNEL (via kernelClient) — the patient shares their own profile with a care
// organization. All routes are /me/* (the caller acts only on themselves); the kernel
// enforces the org_share_policy restriction and returns the policy in the list response so
// the UI can render the restriction without a second call.

import { kernelClient } from "./api";
import { ORG_SHARING_ENDPOINTS } from "./endpoints";

/** One organization the patient can see / share with. */
export interface ShareableOrg {
  org_id: number;
  name: string;
  /** Parent org id if this is a location/child org, else null. */
  parent_id: number | null;
  /** City from the org's details address, when set — shown as "Location · City". */
  city?: string | null;
  /**
   * The location's whole address as one line ("street, line 2, city, state zip") so the patient
   * can confirm it is the right location (F-468). Null for parents / no address on file; older
   * kernels omit it (the row then falls back to the city).
   */
  address?: string | null;
  /** The patient already has a live (accepted) share with this org (only ever a location). */
  is_shared: boolean;
  /**
   * The patient's share lifecycle with this location: 'none' | 'pending' | 'accepted'. 'pending'
   * only occurs under the org's approval mode (SHARE_REQUIRES_APPROVAL) — a request awaiting the
   * org's approval that grants no access yet (is_shared stays false). Drives the "Sent request" /
   * "Awaiting approval" UI. Optional for back-compat with older kernels that omit it (treat a
   * missing value as 'accepted' when is_shared, else 'none').
   */
  share_status?: "none" | "pending" | "accepted";
  /**
   * The patient may share with this org right now. Parents/top-level orgs are NEVER shareable —
   * they are accordion headers; only child LOCATIONS are. Under single_org, a location is
   * shareable only while the patient has no other active share (one location at a time).
   */
  shareable: boolean;
  /**
   * Child locations for a parent/top-level org (parent_id === null). A location row carries an
   * empty array. The Share action lives on each location, never on the parent header.
   */
  locations?: ShareableOrg[];
}

/** The three effective sharing states the kernel resolves org_share_policy into. */
export type SharingPolicyKind = "multi_org" | "single_org" | "blocked";

/** The patient's sharing capability, mirrored from profile.org_share_policy. */
export interface SharingPolicy {
  policy: SharingPolicyKind;
  can_share_multiple: boolean;
  /** For single_org: the only org the patient may share with (their home org), else null. */
  restricted_to_org_id: number | null;
}

export interface ShareableOrgsResponse {
  organizations: ShareableOrg[];
  sharing_policy: SharingPolicy;
}

/** List active organizations plus the caller's sharing policy. */
export const getShareableOrganizations = async (
  search?: string,
): Promise<ShareableOrgsResponse> => {
  const { data } = await kernelClient.get(ORG_SHARING_ENDPOINTS.LIST, {
    params: search ? { search } : undefined,
  });
  return data as ShareableOrgsResponse;
};

/** Share the caller's profile with an org (immediate grant). Idempotent server-side. */
export const shareWithOrganization = async (orgId: number): Promise<void> => {
  await kernelClient.post(ORG_SHARING_ENDPOINTS.SHARE(orgId));
};

/** Revoke a share. Idempotent server-side (204 even if not shared). */
export const unshareWithOrganization = async (orgId: number): Promise<void> => {
  await kernelClient.delete(ORG_SHARING_ENDPOINTS.UNSHARE(orgId));
};
